import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
} from 'recharts';
import ChartFrame from './ChartFrame';

interface DataPoint {
  date: string;
  net_worth: number;
}

interface Props {
  data: DataPoint[];
}

const formatCurrency = (value: number) =>
  Math.abs(value) >= 1000 ? `$${(value / 1000).toFixed(1)}k` : `$${value.toFixed(0)}`;

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const NetWorthChart: React.FC<Props> = ({ data }) => {
  const [showRaw, setShowRaw] = useState(false);

  if (!data.length) {
    return (
      <div className="chart-wrapper">
        <p className="chart-empty">No balance data available.</p>
      </div>
    );
  }

  const renderChart = (fullscreen: boolean) => (
    <ResponsiveContainer width="100%" height={fullscreen ? '100%' : 280}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
        <XAxis
          dataKey="date"
          tickFormatter={formatDate}
          tick={{ fontSize: 11 }}
          interval="preserveStartEnd"
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 11 }}
          width={58}
        />
        <Tooltip
          formatter={(value: number) => [`$${value.toFixed(2)}`, 'Net Worth']}
          labelFormatter={formatDate}
        />
        <Line
          type="monotone"
          dataKey="net_worth"
          stroke="#667eea"
          strokeWidth={2.5}
          dot={false}
          activeDot={{ r: 5, fill: '#764ba2' }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderTable = () => (
    <table className="chart-data-table">
      <thead>
        <tr><th>Date</th><th>Net Worth</th></tr>
      </thead>
      <tbody>
        {data.map((d, i) => (
          <tr key={i}>
            <td>{d.date}</td>
            <td>${d.net_worth.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <ChartFrame
      title="Net Worth Over Time"
      showRaw={showRaw}
      onToggle={setShowRaw}
      renderChart={renderChart}
      renderTable={renderTable}
    />
  );
};

export default NetWorthChart;
